import { Injectable } from '@angular/core';
import { FormControl, ValidatorFn, Validators } from '@angular/forms';
import {
  DEMO_EMPLOYMENT_TYPE_OPTIONS,
  DEMO_HOUSING_TYPE_OPTIONS,
  DEMO_YES_NO_OPTIONS,
} from '../../core/config/demo-validation.config';
import { ArrayRadioButton, HybridModel } from '../../shared/models/hybrid-model/hybrid-model';
import { CardInfoModel } from '../../shared/components-generals/card-info/card-info';

// Card de navegación hacia un formulario dinámico (sin evidencia asociada)
export interface FormNavigationCardModel extends CardInfoModel {
  key: string;
}

// Card de captura de evidencia; code identifica el closing field y evidenceType el tipo enviado al backend
export interface CaptureCardModel extends CardInfoModel {
  key: string;
  code: string;
  evidenceType: string;
  cameraModalTitle?: string;
  cameraModalText?: string;
  cameraPreviewText?: string;
  captureOrientation?: 'portrait' | 'landscape';
  preferredFacingMode?: 'user' | 'environment';
  frame?: 'circle' | 'document';
}

const TEXT_PATTERN = '[a-zA-Z0-9áéíóúÁÉÍÓÚÑñ\\ \\.\\@_\\-#$%&\\/:;\\(\\)\\+]{0,255}';
const LONG_TEXT_PATTERN = '[a-zA-Z0-9áéíóúÁÉÍÓÚÑñ\\ \\.\\@_\\-#$%&\\/:;\\(\\)\\+]{0,800}';
const MONEY_PATTERN = '(([1-9],)?([0-9]{0,3},){0,3}[0-9]{3}|[0-9]{0,16})(\\.[0-9]{0,2})?';
const LOCATION_TEXT_PATTERN = '[A-Z0-9Ñ\\ ]{0,255}';
const TEN_DIGITS_PATTERN = /^\d{10}$/;
const FIVE_DIGITS_PATTERN = /^\d{5}$/;
const HOUSEHOLD_SIZE_PATTERN = '[\\d]{0,10}';

const TENURE_OPTIONS = [
  'Menos de 1 año',
  '1 a 2 años',
  '3 a 5 años',
  '6 a 10 años',
  '11 a 15 años',
  '16 a 20 años',
  'Mas de 20 años',
] as const;

const RELATIONSHIP_OPTIONS = [
  'Familiar',
  'Amigo(a)',
  'Compañero(a) de trabajo',
  'Conocido',
] as const;

const VPN_OPTIONS = ['Si', 'No'] as const;

const FORM_KEYS = {
  work: {
    currentlyWorking: '104',
    company: '105',
    position: '106',
    tenure: '107',
    employmentType: '108',
  },
  income: {
    monthlyIncome: '109',
    hasAdditionalIncome: '110',
    additionalIncomeAmount: '111',
    hasFinancialObligations: '112',
    hasActiveCredits: '113',
    activeCreditsAmount: '114',
  },
  address: {
    street: '115-street',
    externalNumber: '115-external-number',
    visualReference: '116',
    housingType: '117',
    tenure: '118',
    householdSize: '119',
    services: '120',
    habitability: '121',
    neighborhood: '136',
    postalCode: '137',
    municipality: '138',
  },
  references: {
    reference1: {
      fullName: '122',
      relationship: '123',
      monthsKnown: '124',
      phone: '125',
      contactConsent: 'reference-1-contact-consent',
    },
    reference2: {
      fullName: '132',
      relationship: '133',
      monthsKnown: '134',
      phone: '135',
      contactConsent: 'reference-2-contact-consent',
    },
  },
  locations: {
    user: '142',
    form: '143',
    evidences: '144',
    vpn: '145',
  },
  evidences: {
    ineFront: '102',
    ineBack: '103',
    facade: '126',
    exteriorNumber: '127',
    street: '128',
    selfie: '141',
  },
} as const;

const mapCardOptionsToDescriptionValue = (
  options: ReadonlyArray<ArrayRadioButton>,
): ArrayRadioButton[] => options.map((option) => ({ ...option, value: option.description }));

const YES_NO_CARD_OPTIONS = mapCardOptionsToDescriptionValue(DEMO_YES_NO_OPTIONS);
const EMPLOYMENT_TYPE_CARD_OPTIONS = mapCardOptionsToDescriptionValue(DEMO_EMPLOYMENT_TYPE_OPTIONS);
const HOUSING_TYPE_CARD_OPTIONS = mapCardOptionsToDescriptionValue(DEMO_HOUSING_TYPE_OPTIONS);

const cloneRadioOptions = (options: ReadonlyArray<ArrayRadioButton>): ArrayRadioButton[] =>
  options.map((option) => ({ ...option }));

const cloneStringOptions = (options: ReadonlyArray<string>): string[] => [...options];

const WORK_FORM_MODELS: HybridModel[] = [
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.work.currentlyWorking,
    label: '¿Actualmente trabajas?',
    placeholder: '¿Actualmente trabajas?',
    type: 'input-general-cards',
    inputSelectRadioButton: {
      value: 'value',
      description: 'description',
    },
    array: cloneRadioOptions(YES_NO_CARD_OPTIONS),
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
      Validators.pattern(TEXT_PATTERN),
    ]),
    key: FORM_KEYS.work.company,
    label: '¿Dónde trabajas?',
    placeholder: 'Ingresa tu trabajo',
    type: 'input-general',
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
      Validators.pattern(TEXT_PATTERN),
    ]),
    key: FORM_KEYS.work.position,
    label: '¿Cuál es tu puesto?',
    placeholder: 'Ej. Gerente',
    type: 'input-general',
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.work.tenure,
    label: '¿Cuánto tiempo llevas en el puesto?',
    placeholder: 'Ej. 2 años',
    type: 'input-general-select',
    array: cloneStringOptions(TENURE_OPTIONS),
    selectedValue: { value: '', description: '' },
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.work.employmentType,
    label: '¿Cómo estás contratado?',
    placeholder: '¿Cómo estás contratado?',
    type: 'input-general-cards',
    inputSelectRadioButton: {
      value: 'value',
      description: 'description',
    },
    array: cloneRadioOptions(EMPLOYMENT_TYPE_CARD_OPTIONS),
    value: '',
    colFull: true,
    labelTop: true,
  },
];

const INCOME_FORM_MODELS: HybridModel[] = [
  {
    control: new FormControl('', [
      Validators.required,
      Validators.pattern(MONEY_PATTERN),
      Validators.min(1),
    ]),
    key: FORM_KEYS.income.monthlyIncome,
    label: '¿Cuánto ganas al mes?',
    placeholder: '$ MXN',
    type: 'input-general-currency',
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.income.hasAdditionalIncome,
    label: '¿Tienes otro ingreso?',
    placeholder: '¿Tienes otro ingreso?',
    type: 'input-general-cards',
    inputSelectRadioButton: {
      value: 'value',
      description: 'description',
    },
    array: cloneRadioOptions(YES_NO_CARD_OPTIONS),
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [
      Validators.required,
      Validators.pattern(MONEY_PATTERN),
      Validators.min(1),
    ]),
    key: FORM_KEYS.income.additionalIncomeAmount,
    label: '¿Cuánto es ese ingreso al mes?',
    placeholder: '$ MXN',
    type: 'input-general-currency',
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.income.hasFinancialObligations,
    label: '¿Cuenta con otras obligaciones financieras?',
    placeholder: '¿Cuenta con otras obligaciones financieras?',
    type: 'input-general-cards',
    inputSelectRadioButton: {
      value: 'value',
      description: 'description',
    },
    array: cloneRadioOptions(YES_NO_CARD_OPTIONS),
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.income.hasActiveCredits,
    label: '¿Tiene créditos vigentes?',
    placeholder: '¿Tiene créditos vigentes?',
    type: 'input-general-cards',
    inputSelectRadioButton: {
      value: 'value',
      description: 'description',
    },
    array: cloneRadioOptions(YES_NO_CARD_OPTIONS),
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [
      Validators.required,
      Validators.pattern(MONEY_PATTERN),
      Validators.min(1),
    ]),
    key: FORM_KEYS.income.activeCreditsAmount,
    label: '¿Cuál es el monto aproximado que paga mensualmente por sus créditos?',
    placeholder: '$ MXN',
    type: 'input-general-currency',
    value: '',
    colFull: true,
    labelTop: true,
  },
];

const ADDRESS_FORM_MODELS: HybridModel[] = [
  {
    control: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
      Validators.pattern(TEXT_PATTERN),
    ]),
    key: FORM_KEYS.address.street,
    label: 'Dirección del domicilio',
    placeholder: 'Calle',
    type: 'input-general',
    value: '',
    colFull: true,
  },
  {
    control: new FormControl('', [Validators.required, Validators.min(0)]),
    key: FORM_KEYS.address.externalNumber,
    label: '',
    placeholder: 'Número exterior',
    type: 'input-general-number',
    value: '',
    labelTop: false,
  },
  {
    control: new FormControl('', [Validators.required, Validators.pattern(FIVE_DIGITS_PATTERN)]),
    key: FORM_KEYS.address.postalCode,
    label: '',
    placeholder: 'C.P.',
    type: 'input-general',
    value: '',
    labelTop: false,
  },
  {
    control: new FormControl({ value: '', disabled: true }, [
      Validators.required,
      Validators.minLength(3),
    ]),
    key: FORM_KEYS.address.municipality,
    label: '',
    placeholder: 'Municipio',
    type: 'input-general',
    value: '',
    disabled: true,
    colFull: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.address.neighborhood,
    label: '',
    placeholder: 'Colonia',
    type: 'input-general-select',
    array: [],
    selectedValue: { value: 'Locality', description: 'Locality' },
    value: '',
    colFull: true,
    labelTop: false,
  },
  {
    control: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
      Validators.pattern(LONG_TEXT_PATTERN),
    ]),
    key: FORM_KEYS.address.visualReference,
    label: '¿Hay alguna referencia visual para llegar al domicilio?',
    placeholder: 'Ej. casa rosa',
    type: 'input-general',
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.address.housingType,
    label: '¿Cómo es tu casa?',
    placeholder: '¿Cómo es tu casa?',
    type: 'input-general-cards',
    inputSelectRadioButton: {
      value: 'value',
      description: 'description',
    },
    array: cloneRadioOptions(HOUSING_TYPE_CARD_OPTIONS),
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.address.tenure,
    label: '¿Cuánto tiempo llevas en el domicilio?',
    placeholder: 'Ej. 2 años',
    type: 'input-general-select',
    array: cloneStringOptions(TENURE_OPTIONS),
    selectedValue: { value: '', description: '' },
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [Validators.required, Validators.pattern(HOUSEHOLD_SIZE_PATTERN)]),
    key: FORM_KEYS.address.householdSize,
    label: '¿Cuántos viven en tu casa?',
    placeholder: 'Ej. 5',
    type: 'input-general',
    value: '',
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.address.services,
    label: 'Selecciona con qué servicios cuenta',
    placeholder: 'Selecciona con qué servicios cuenta',
    type: 'input-general-checkbox-column',
    value: '',
    selectedValue: {
      value: 'codigo',
      description: 'nombre',
    },
    array: [],
    colFull: true,
    labelTop: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.address.habitability,
    label: '¿La vivienda presenta condiciones adecuadas de habitabilidad?',
    placeholder: '¿La vivienda presenta condiciones adecuadas de habitabilidad?',
    type: 'input-general-cards',
    inputSelectRadioButton: {
      value: 'value',
      description: 'description',
    },
    array: cloneRadioOptions(YES_NO_CARD_OPTIONS),
    value: '',
    colFull: true,
    labelTop: true,
  },
];

const REFERENCE_1_FORM_MODELS: HybridModel[] = [
  {
    control: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
      Validators.pattern(TEXT_PATTERN),
    ]),
    key: FORM_KEYS.references.reference1.fullName,
    label: '',
    placeholder: 'Nombre completo',
    type: 'input-general',
    value: '',
    colFull: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.references.reference1.relationship,
    label: '',
    placeholder: 'Relación',
    type: 'input-general-select',
    array: cloneStringOptions(RELATIONSHIP_OPTIONS),
    selectedValue: { value: '', description: '' },
    value: '',
    colFull: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.references.reference1.monthsKnown,
    label: '',
    placeholder: 'Meses de conocerle',
    type: 'input-general-select',
    array: cloneStringOptions(TENURE_OPTIONS),
    selectedValue: { value: '', description: '' },
    value: '',
    colFull: true,
  },
  {
    control: new FormControl('', [Validators.required, Validators.pattern(TEN_DIGITS_PATTERN)]),
    key: FORM_KEYS.references.reference1.phone,
    label: '',
    placeholder: 'Número de teléfono',
    type: 'input-general-phone',
    value: '',
    colFull: true,
  },
  {
    control: new FormControl(false, [Validators.requiredTrue]),
    key: FORM_KEYS.references.reference1.contactConsent,
    label: 'Acepto que contacten a esta persona',
    placeholder: '',
    type: 'input-general-checkbox',
    value: false,
    colFull: true,
  },
];

const REFERENCE_2_FORM_MODELS: HybridModel[] = [
  {
    control: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
      Validators.pattern(TEXT_PATTERN),
    ]),
    key: FORM_KEYS.references.reference2.fullName,
    label: '',
    placeholder: 'Nombre completo',
    type: 'input-general',
    value: '',
    colFull: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.references.reference2.relationship,
    label: '',
    placeholder: 'Relación',
    type: 'input-general-select',
    array: cloneStringOptions(RELATIONSHIP_OPTIONS),
    selectedValue: { value: '', description: '' },
    value: '',
    colFull: true,
  },
  {
    control: new FormControl('', [Validators.required]),
    key: FORM_KEYS.references.reference2.monthsKnown,
    label: '',
    placeholder: 'Meses de conocerle',
    type: 'input-general-select',
    array: cloneStringOptions(TENURE_OPTIONS),
    selectedValue: { value: '', description: '' },
    value: '',
    colFull: true,
  },
  {
    control: new FormControl('', [Validators.required, Validators.pattern(TEN_DIGITS_PATTERN)]),
    key: FORM_KEYS.references.reference2.phone,
    label: '',
    placeholder: 'Número de teléfono',
    type: 'input-general-phone',
    value: '',
    colFull: true,
  },
  {
    control: new FormControl(false, [Validators.requiredTrue]),
    key: FORM_KEYS.references.reference2.contactConsent,
    label: 'Acepto que contacten a esta persona',
    placeholder: '',
    type: 'input-general-checkbox',
    value: false,
    colFull: true,
  },
];

const USER_LOCATION_FORM_MODELS: HybridModel[] = [
  {
    control: new FormControl('', [
      Validators.required,
      Validators.pattern(
        /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/,
      ),
    ]),
    key: FORM_KEYS.locations.user,
    label: 'Latitud-Longitud User',
    placeholder: 'Latitud-Longitud User',
    type: 'input-general',
    value: '',
    colFull: true,
    labelTop: true,
  },
];

const FORM_LOCATION_FORM_MODELS: HybridModel[] = [
  {
    control: new FormControl('', [
      Validators.required,
      Validators.pattern(
        /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/,
      ),
    ]),
    key: FORM_KEYS.locations.form,
    label: 'Latitud-Longitud Formulario',
    placeholder: 'Latitud-Longitud Formulario',
    type: 'input-general',
    value: '',
    colFull: true,
    labelTop: true,
  },
];

const EVIDENCES_LOCATION_FORM_MODELS: HybridModel[] = [
  {
    control: new FormControl('', [
      Validators.required,
      Validators.pattern(
        /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/,
      ),
    ]),
    key: FORM_KEYS.locations.evidences,
    label: 'Latitud-Longitud Evidencias',
    placeholder: 'Latitud-Longitud Evidencias',
    type: 'input-general',
    value: '',
    colFull: true,
    labelTop: true,
  },
];

const IS_VPN: HybridModel[] = [
  {
    control: new FormControl('No'),
    key: FORM_KEYS.locations.vpn,
    label: '¿El Usuario Se Conecto A VPN?',
    placeholder: '¿El Usuario Se Conecto A VPN?',
    type: 'input-general-select',
    array: cloneStringOptions(VPN_OPTIONS),
    selectedValue: { value: '', description: '' },
    value: 'No',
    colFull: true,
    labelTop: true,
  },
];

// Todos los HybridModel mutables del flujo adout-user; se usan para capturar/restaurar su estado inicial.
const ALL_ADOUT_USER_FORM_MODELS: HybridModel[] = [
  ...WORK_FORM_MODELS,
  ...INCOME_FORM_MODELS,
  ...ADDRESS_FORM_MODELS,
  ...REFERENCE_1_FORM_MODELS,
  ...REFERENCE_2_FORM_MODELS,
  ...USER_LOCATION_FORM_MODELS,
  ...FORM_LOCATION_FORM_MODELS,
  ...EVIDENCES_LOCATION_FORM_MODELS,
  ...IS_VPN,
];

const ADOUT_USER_MAIN_CARDS: FormNavigationCardModel[] = [
  {
    key: 'work',
    titule: 'Trabajo',
    text: '',
    icon: 'work',
    colorIcon: '#548e43',
    bg: '#e9f0d1',
    isButton: true,
    value: false,
  },
  {
    key: 'income',
    titule: 'Ingresos',
    text: '',
    icon: 'attach_money',
    colorIcon: '#4b0bb1',
    bg: '#dbceef',
    isButton: true,
    value: false,
  },
  {
    key: 'address',
    titule: 'Domicilio',
    text: '',
    icon: 'home',
    colorIcon: '#009fa5',
    bg: '#c4e4e4',
    isButton: true,
    value: false,
  },
  {
    key: 'references',
    titule: 'Referencias',
    text: '',
    icon: 'person',
    colorIcon: '#ff8408',
    bg: '#f8f5d9',
    isButton: true,
    value: false,
  },
];

const OFFICIAL_ID_CARDS: CaptureCardModel[] = [
  {
    key: 'front',
    code: FORM_KEYS.evidences.ineFront,
    evidenceType: 'IneFrente',
    titule: 'INE Frente',
    text: '',
    icon: 'id_card',
    colorIcon: '#c6ba17',
    bg: '#fbf7b7',
    isButton: true,
    value: false,
    cameraModalTitle: 'Foto frente',
    cameraModalText: 'Alinea la identificación en el recuadro',
    cameraPreviewText: 'Asegúrate de que esté vigente y sin daños visibles',
    captureOrientation: 'landscape',
    preferredFacingMode: 'environment',
    frame: 'document',
  },
  {
    key: 'back',
    code: FORM_KEYS.evidences.ineBack,
    evidenceType: 'IneReverso',
    titule: 'INE Reverso',
    text: '',
    icon: 'id_card',
    colorIcon: '#4b0bb1',
    bg: '#dbceef',
    isButton: true,
    value: false,
    cameraModalTitle: 'Foto reverso',
    cameraModalText: 'Alinea la identificación en el recuadro',
    cameraPreviewText: 'Asegúrate de que esté vigente y sin daños visibles',
    captureOrientation: 'landscape',
    preferredFacingMode: 'environment',
    frame: 'document',
  },
];

const BIOMETRICS_INFO_CARDS: CardInfoModel[] = [
  {
    titule: 'Rostro descubierto',
    text: 'Mantén el rostro descubierto y viendo al frente',
    icon: 'face',
    colorIcon: '#548e43',
    bg: '#e9f0d1',
  },
  {
    titule: 'Buena iluminación',
    text: 'Busca tener un espacio privado y bien iluminado',
    icon: 'light_mode',
    colorIcon: '#4b0bb1',
    bg: '#dbceef',
  },
];

const ABROAD_CARDS: CaptureCardModel[] = [
  {
    key: 'facade',
    code: FORM_KEYS.evidences.facade,
    evidenceType: 'FachadaCompleta',
    titule: 'Fachada',
    text: '',
    icon: 'home',
    colorIcon: '#548e43',
    bg: '#e9f0d1',
    isButton: true,
    value: false,
  },
  {
    key: 'exteriorNumber',
    code: FORM_KEYS.evidences.exteriorNumber,
    evidenceType: 'NumeroExterior',
    titule: 'Número exterior',
    text: '',
    icon: '123',
    colorIcon: '#c76e06',
    bg: '#fbd9b7',
    isButton: true,
    value: false,
  },
  {
    key: 'street',
    code: FORM_KEYS.evidences.street,
    evidenceType: 'CalleDomicilio',
    titule: 'Calle del domicilio',
    text: '',
    icon: 'road',
    colorIcon: '#4b0bb1',
    bg: '#dbceef',
    isButton: true,
    value: false,
  },
];

const ADDRESS_VALIDATION_INFO_CARDS: CardInfoModel[] = [
  {
    titule: 'Fotografías en vivo',
    text: 'No se aceptan fotos de tu galería',
    icon: 'photo_camera',
    colorIcon: '#548e43',
    bg: '#e9f0d1',
    isButton: false,
    value: false,
  },
  {
    titule: 'Buena iluminación',
    text: 'Busca tener un espacio iluminado',
    icon: 'light_mode',
    colorIcon: '#4b0bb1',
    bg: '#dbceef',
    isButton: false,
    value: false,
  },
];

const cloneCards = <T>(cards: readonly T[]): T[] => cards.map((card) => ({ ...card }));

@Injectable({
  providedIn: 'root',
})
export class AdoutUserFormsService {
  public readonly formKeys = FORM_KEYS;

  public getWorkForm(): HybridModel[] {
    return WORK_FORM_MODELS;
  }

  public getIncomeForm(): HybridModel[] {
    return INCOME_FORM_MODELS;
  }

  public getAddressForm(): HybridModel[] {
    return ADDRESS_FORM_MODELS;
  }

  public getReference1Form(): HybridModel[] {
    return REFERENCE_1_FORM_MODELS;
  }

  public getReference2Form(): HybridModel[] {
    return REFERENCE_2_FORM_MODELS;
  }

  public getUserLocationForm(): HybridModel[] {
    return USER_LOCATION_FORM_MODELS;
  }

  public getFormLocationForm(): HybridModel[] {
    return FORM_LOCATION_FORM_MODELS;
  }

  public getEvidencesLocationForm(): HybridModel[] {
    return EVIDENCES_LOCATION_FORM_MODELS;
  }

  public getVpnForm(): HybridModel[] {
    return IS_VPN;
  }

  public getAdoutUserMainCards(): FormNavigationCardModel[] {
    return cloneCards(ADOUT_USER_MAIN_CARDS);
  }

  public getOfficialIdCards(): CaptureCardModel[] {
    return cloneCards(OFFICIAL_ID_CARDS);
  }

  public getBiometricsInfoCards(): CardInfoModel[] {
    return cloneCards(BIOMETRICS_INFO_CARDS);
  }

  public getAbroadCards(): CaptureCardModel[] {
    return cloneCards(ABROAD_CARDS);
  }

  public getAddressValidationInfoCards(): CardInfoModel[] {
    return cloneCards(ADDRESS_VALIDATION_INFO_CARDS);
  }

  /** Restaura los HybridModel de adout-user a su estado inicial (equivalente a recargar la app). */
  public resetFormsState(): void {
    for (const model of ALL_ADOUT_USER_FORM_MODELS) {
      model.control.reset();
    }
  }
}
